import {doAuthenticatedQuery} from "src/services/shared/doQuery";
import {portalUserRoles} from "../../utils/constants";
import {
  clientNotificationSettingsQuery,
  updateClientNotificationSettingsMutation
} from "./ClientsGraphQL";
import {graphQLClient} from "../shared/GraphQLService";
import {QueryContext} from "../../hooks/TanstackHooks";
import {nullCollapsableClientPubId} from "../shared/nullCollapsableUtilities";
import {IClientNotificationSettings} from "../../types/ClientNotificationSettings";
import {GraphQLClient} from "graphql-request";

// The shape of cfg.client.configuration, as far as the notification settings go
type RawClientConfiguration = {
  timezone?: string,
  summary?: { email?: string[] },
  helpdesk?: {
    helpdesk_email?: string,
    features?: { inactive_users?: boolean, zombie_users?: boolean, new_licensed_user?: boolean },
  }[],
  slack?: {
    features?: {
      connection_created?: boolean,
      connection_updated?: boolean,
      connection_login_failed?: boolean,
      hpu_mfa_disabled?: boolean,
    },
  },
}

type RawClient = { pub_id: string, name: string, configuration: RawClientConfiguration | null };

// Maps cfg.client.configuration to the notification settings.
// Flags are read under the names the postoffice reads them by: a helpdesk
// entry written by the 20251103 migration carries "new_licensed_users", which
// the postoffice ignores, so it shows as off here.
export const transformClientNotificationSettings = (
  client: RawClient
): IClientNotificationSettings => {
  const configuration = client.configuration ?? {};
  const slack = configuration.slack;

  return {
    clientId: client.pub_id,
    clientName: client.name,
    timezone: configuration.timezone ?? '',
    summaryEmails: configuration.summary?.email ?? [],
    helpdesk: (configuration.helpdesk ?? []).map(h => ({
      email: h.helpdesk_email ?? '',
      inactiveUsers: !!h.features?.inactive_users,
      zombieUsers: !!h.features?.zombie_users,
      newLicensedUser: !!h.features?.new_licensed_user,
    })),
    slackFeatures: slack && typeof slack === 'object' ? {
      connectionCreated: !!slack.features?.connection_created,
      connectionUpdated: !!slack.features?.connection_updated,
      connectionLoginFailed: !!slack.features?.connection_login_failed,
      hpuMfaDisabled: !!slack.features?.hpu_mfa_disabled,
    } : undefined,
  };
}

// Gets the notification settings of parameters.clientId, or of the current
// client when there is none. Super admins query as the admin role so they can
// read any client.
export const getClientNotificationSettings = async ({accessToken, queryContext, parameters, userRole}: {
  accessToken: string,
  queryContext: QueryContext,
  parameters: { clientId?: string },
  userRole?: string,
}): Promise<IClientNotificationSettings> => {
  const clientId = parameters?.clientId ?? queryContext.client.id;
  const theQuery = async (client: GraphQLClient) => {
    const result = client.request<{ client: RawClient[] }>(
      clientNotificationSettingsQuery,
      nullCollapsableClientPubId(clientId),
      {
        'x-hasura-role': userRole === portalUserRoles.admin ? portalUserRoles.admin : portalUserRoles.user,
      },
    );
    return result;
  }
  const response = await doAuthenticatedQuery(
    theQuery, accessToken
  );

  if (response?.client?.length !== 1) {
    throw new Error('Exactly one client should be returned');
  }

  return transformClientNotificationSettings(response.client[0]);
}

export const updateClientNotificationSettings = async ({
  hasuraAccessToken,
  client_id,
  settings,
  role
}: {
  hasuraAccessToken: string,
  client_id: string,
  settings: IClientNotificationSettings,
  role?: string,
}) => {
  if (!settings) throw new Error('settings required');

  const gqlClient = await graphQLClient({accessToken: hasuraAccessToken});

  if (role === portalUserRoles.admin) {
    gqlClient.setHeader("x-hasura-role", role);
  }

  const variables = {
    client_id: client_id,
    req: {
      id: settings.clientId,
      timezone: settings.timezone || null,
      summary_emails: settings.summaryEmails,
      helpdesk: settings.helpdesk.map(h => ({
        email: h.email,
        inactive_users: h.inactiveUsers,
        zombie_users: h.zombieUsers,
        new_licensed_user: h.newLicensedUser,
      })),
      slack_features: settings.slackFeatures ? {
        connection_created: settings.slackFeatures.connectionCreated,
        connection_updated: settings.slackFeatures.connectionUpdated,
        connection_login_failed: settings.slackFeatures.connectionLoginFailed,
        hpu_mfa_disabled: settings.slackFeatures.hpuMfaDisabled,
      } : null,
    }
  };

  const result = await gqlClient?.request(
    updateClientNotificationSettingsMutation,
    variables
  )
  return result;
}
